import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "xterm/css/xterm.css";

// initiate / start / end / auth flows, but keeps the terminal wiring

const RemoteTerminal = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const terminalContainerRef = useRef(null);
  const termRef = useRef(null);
  const wsRequestRef = useRef(null);
  const wsResponseRef = useRef(null);
  const effectCleanupRef = useRef(null);

  const [hubName, setHubName] = useState("");
  const [sessionToken, setSessionToken] = useState(null);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [isDeviceReady, setIsDeviceReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingSession, setIsPreparingSession] = useState(false);
  const [hasPreClearedSessions, setHasPreClearedSessions] = useState(false);
  const [isClearingSessions, setIsClearingSessions] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [wsRequestEndpoint, setWsRequestEndpoint] = useState(null);
  const [wsResponseEndpoint, setWsResponseEndpoint] = useState(null);

  const authToken = localStorage.getItem("accessToken");
  const REMOTE_TERMINAL_API_URL =
    process.env.REACT_APP_REMOTE_TERMINAL_URL ||
    "http://localhost:8082/device_mgmt_service";

  // WebSocket base URL (e.g., wss://dev.mangoe.com/api/device_mgmt_service)
  const REMOTE_TERMINAL_WS_BASE =
    process.env.REACT_APP_REMOTE_TERMINAL_WS_BASE ||
    "ws://localhost:8082/device_mgmt_service";

  const initiationAttemptedRef = useRef(false);
  const isPopoutMode =
    new URLSearchParams(window.location.search).get("popout") === "1";

  const generateSessionToken = () =>
    "token_" +
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);

  // 1) Get hubName from navigation state and generate session token
  useEffect(() => {
    // Prefer hubName from navigation state, otherwise support ?hubName=... for pop-out windows
    const stateHubName = location.state?.hubName;
    const searchParams = new URLSearchParams(window.location.search);
    const queryHubName = searchParams.get("hubName");

    const finalHubName = stateHubName || queryHubName;

    if (finalHubName) {
      setHubName(finalHubName);
      setSessionToken(generateSessionToken());
    } else {
      toast.warn("No device selected. Redirecting...");
      setTimeout(() => navigate("/devices"), 2000);
    }
  }, [location, navigate]);

  // 2) Call initiate API to get WS endpoints
  useEffect(() => {
    if (
      !hubName ||
      !sessionToken ||
      !authToken ||
      !hasPreClearedSessions ||
      initiationAttemptedRef.current
    ) {
      return;
    }

    initiationAttemptedRef.current = true;
    setIsLoading(true);
    setConnectionError(null);

    let isActive = true;

    const initiateConnection = async () => {
      if (effectCleanupRef.current) {
        effectCleanupRef.current.abort();
      }

      const controller = new AbortController();
      effectCleanupRef.current = controller;

      try {
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(
          `${REMOTE_TERMINAL_API_URL}/api/remote-terminal/initiate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            signal: controller.signal,
            body: JSON.stringify({
              thingName: hubName,
              session_token: sessionToken,
            }),
          }
        );

        clearTimeout(timeoutId);

        if (!isActive) return;

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(
            `Initiate API failed with status ${response.status}: ${errorData}`
          );
        }

        const data = await response.json();
        const responseData = data.data || data;

        if (!data.success || !responseData.wsRequestEndpoint || !responseData.wsResponseEndpoint) {
          throw new Error(responseData.message || "Failed to get WebSocket endpoints");
        }

        if (isActive) {
          setWsRequestEndpoint(responseData.wsRequestEndpoint);
          setWsResponseEndpoint(responseData.wsResponseEndpoint);
        }
      } catch (error) {
        if (!isActive) return;

        console.error("Initiate API error:", error);

        if (error.name === "AbortError") {
          setConnectionError("Request timeout - Backend may not be responding");
          toast.error("Request timeout - Please check if backend is running");
        } else if (error.message.includes("Failed to fetch") || error instanceof TypeError) {
          setConnectionError(`Network Error - Cannot reach ${REMOTE_TERMINAL_API_URL}`);
          toast.error(
            `Backend not reachable - Check CORS settings and backend status`
          );
        } else {
          setConnectionError(error.message);
          toast.error(`Connection failed: ${error.message}`);
        }

        initiationAttemptedRef.current = false;
        setIsLoading(false);
      }
    };

    initiateConnection();

    return () => {
      isActive = false;
      if (effectCleanupRef.current) {
        effectCleanupRef.current.abort();
      }
    };
  }, [hubName, sessionToken, authToken, REMOTE_TERMINAL_API_URL, hasPreClearedSessions]);

  // For pop-out flow: clear all existing terminal sessions first, then show login.
  useEffect(() => {
    if (!hubName || !authToken) {
      return;
    }

    let isActive = true;

    const prepareSession = async () => {
      if (!isPopoutMode) {
        if (isActive) setHasPreClearedSessions(true);
        return;
      }

      setIsPreparingSession(true);
      try {
        await sendEndCommand(null);
      } finally {
        if (isActive) {
          setHasPreClearedSessions(true);
          setIsPreparingSession(false);
        }
      }
    };

    prepareSession();

    return () => {
      isActive = false;
    };
  }, [hubName, authToken, isPopoutMode]);

  // 3) Open request/response WebSockets and send Start command
  useEffect(() => {
    if (!wsRequestEndpoint || !wsResponseEndpoint || isWebSocketConnected || !authToken) {
      return;
    }

    try {
      // Build WS URLs using configured WS base so path prefixes like "/api"
      // are preserved even when backend returns endpoints such as
      // "/device_mgmt_service/...".
      const wsBaseUrl = new URL(REMOTE_TERMINAL_WS_BASE);
      const wsBasePath = wsBaseUrl.pathname.replace(/\/+$/, "");

      const buildWsUrl = (endpoint) => {
        if (!endpoint) return null;
        if (endpoint.startsWith("ws://") || endpoint.startsWith("wss://")) {
          // endpoint already contains full WS URL
          return `${endpoint}${endpoint.includes("?") ? "&" : "?"}access_token=${authToken}`;
        }

        const normalizedEndpoint = endpoint.startsWith("/")
          ? endpoint
          : `/${endpoint}`;

        const endpointWithBase =
          wsBasePath &&
          normalizedEndpoint !== wsBasePath &&
          !normalizedEndpoint.startsWith(`${wsBasePath}/`)
            ? `${wsBasePath}${normalizedEndpoint}`
            : normalizedEndpoint;

        // endpoint is path-like, build against configured WS origin/base
        return `${wsBaseUrl.protocol}//${wsBaseUrl.host}${endpointWithBase}${
          endpoint.includes("?") ? "&" : "?"
        }access_token=${authToken}`;
      };

      const wsRequestUrl = buildWsUrl(wsRequestEndpoint);
      const wsResponseUrl = buildWsUrl(wsResponseEndpoint);

      const newWsRequest = new WebSocket(wsRequestUrl);
      newWsRequest.binaryType = "arraybuffer";

      const newWsResponse = new WebSocket(wsResponseUrl);
      newWsResponse.binaryType = "arraybuffer";

      newWsRequest.onopen = () => {
        console.log("Request WebSocket connected");
      };

      newWsRequest.onmessage = (evt) => {
        try {
          let payload;
          if (evt.data instanceof ArrayBuffer) {
            payload = new TextDecoder("utf-8").decode(new Uint8Array(evt.data));
          } else {
            payload = evt.data;
          }
          console.log("<< Request WS:", payload);
        } catch (e) {
          console.error("Error logging request WS message:", e);
        }
      };

      newWsResponse.onopen = () => {
        console.log("Response WebSocket connected");
        console.log("Request WebSocket readyState:", newWsRequest.readyState);
        setIsWebSocketConnected(true);
        wsRequestRef.current = newWsRequest;
        wsResponseRef.current = newWsResponse;
        console.log("✅ wsRequestRef.current set:", wsRequestRef.current);
        sendStartCommand();
      };

      newWsResponse.onmessage = (evt) => {
        let payload;
        if (evt.data instanceof ArrayBuffer) {
          payload = new TextDecoder("utf-8").decode(new Uint8Array(evt.data));
        } else {
          payload = evt.data;
        }

        // Handle connection/auth control messages if JSON
        if (typeof payload === "string") {
          try {
            const msg = JSON.parse(payload);
            if (msg.type === "connection") {
              setIsDeviceReady(true);
              setIsLoading(false);
              return;
            }
            if (msg.type === "auth-response") {
              const authData =
                typeof msg.data === "string" ? JSON.parse(msg.data) : msg.data;
              if (authData?.status === "OK") {
                setIsLoggedIn(true);
                toast.success("Login successful");
              } else {
                toast.error("Login failed");
              }
              setIsLoading(false);
              return;
            }
            if (msg.type === "output") {
              // Extract actual terminal output from the message
              let output = msg.data;
              if (typeof output === "string") {
                try {
                  const maybeJson = JSON.parse(output);
                  if (typeof maybeJson === "string") {
                    output = maybeJson;
                  }
                } catch {
                  // normal string
                }
              }
              // Write the output to terminal
              if (termRef.current && output) {
                termRef.current.write(output);
              }
              return;
            }
            if (msg.type === "error") {
              toast.error(msg.message || "Server error");
              return;
            }
          } catch {
            // Not JSON: fall through to raw terminal output
          }
        }

        // Raw terminal bytes → write to xterm 
        if (termRef.current && payload) {
          termRef.current.write(payload);
        }
      };

      newWsRequest.onclose = () => {
        console.log("Request WebSocket disconnected");
        setIsWebSocketConnected(false);
        setIsDeviceReady(false);
        setIsLoggedIn(false);
      };

      newWsResponse.onclose = () => {
        console.log("Response WebSocket disconnected");
        setIsWebSocketConnected(false);
        setIsDeviceReady(false);
        setIsLoggedIn(false);
        if (termRef.current) {
          termRef.current.write("\r\n[Disconnected from device]\r\n");
        }
      };

      newWsRequest.onerror = (err) => {
        console.error("Request WebSocket error:", err);
        setConnectionError("Request WebSocket connection error");
        toast.error("Request WebSocket connection error");
        setIsLoading(false);
      };

      newWsResponse.onerror = (err) => {
        console.error("Response WebSocket error:", err);
        setConnectionError("Response WebSocket connection error");
        toast.error("Response WebSocket connection error");
        setIsLoading(false);
      };
    } catch (error) {
      console.error("Error creating WebSocket:", error);
      setConnectionError(error.message);
      toast.error("Failed to create WebSocket");
      setIsLoading(false);
    }
  }, [
    wsRequestEndpoint,
    wsResponseEndpoint,
    isWebSocketConnected,
    authToken,
    REMOTE_TERMINAL_WS_BASE,
  ]);

  // 4) After login success, initialize xterm 
  useEffect(() => {
    if (isLoggedIn && !termRef.current && terminalContainerRef.current) {
      initTerminal(wsRequestRef.current);
    }
  }, [isLoggedIn]);

  const sendStartCommand = async () => {
    try {
      const baseUrl = REMOTE_TERMINAL_API_URL.endsWith("/")
        ? REMOTE_TERMINAL_API_URL
        : REMOTE_TERMINAL_API_URL + "/";
      const commandApiUrl = `${baseUrl}command/${hubName}/sendCommand`;

      const response = await fetch(commandApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          command: "Start",
          parameters: {
            session_token: sessionToken,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`SendCommand failed: ${response.status}`);
      }

      setIsDeviceReady(true);
      setIsLoading(false);
    } catch (error) {
      console.error("SendCommand error:", error);
      toast.error(`Failed to send command: ${error.message}`);
    }
  };

  const sendEndCommand = async (sessionTokenToSend) => {
    try {
      const baseUrl = REMOTE_TERMINAL_API_URL.endsWith("/")
        ? REMOTE_TERMINAL_API_URL
        : REMOTE_TERMINAL_API_URL + "/";
      const commandApiUrl = `${baseUrl}command/${hubName}/sendCommand`;

      const response = await fetch(commandApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          command: "End",
          parameters: {
            session_token: sessionTokenToSend,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`SendCommand End failed: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error("SendCommand End error:", error);
      toast.error(`Failed to send end command: ${error.message}`);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    if (!isDeviceReady) {
      toast.error("Device is not ready yet");
      return;
    }

    const form = e.target;
    const username = form.username.value.trim();
    const password = form.password.value;

    if (!username || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);

    try {
      if (!wsRequestRef.current || wsRequestRef.current.readyState !== WebSocket.OPEN) {
        toast.error("Request WebSocket not connected");
        setIsLoading(false);
        return;
      }

      const authCommand = {
        type: "auth",
        username,
        password,
        sessionId: sessionToken,
      };

      wsRequestRef.current.send(JSON.stringify(authCommand));
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Login failed");
      setIsLoading(false);
    }
  };

  const initTerminal = async (wsConnection) => {
    try {
      const container = terminalContainerRef.current;
      if (!container) {
        return;
      }

      const { Terminal } = await import("xterm");

      const term = new Terminal({
        cursorBlink: true,
        fontSize: 13,
        theme: {
          background: "#1e1e1e",
          foreground: "#d4d4d4",
        },
      });

      term.open(container);
      term.focus();

      termRef.current = term;

      term.write("Connected to remote shell\r\n");

      term.onData((data) => {
        // Local echo so typing is visible, while still sending everything
        // to the backend. Handle Enter/backspace nicely.
        if (termRef.current) {
          if (data === "\r") {
            termRef.current.write("\r\n");
          } else if (data === "\u007f") {
            // backspace
            termRef.current.write("\b \b");
          } else {
            termRef.current.write(data);
          }
        }

        // Send terminal input directly via the wsConnection parameter (EXACTLY like RemoteTerminal)
        if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
          try {
            const payload = JSON.stringify({ type: "input", input: data });
            console.log(">> Sending to request WS:", payload);
            wsConnection.send(payload);
          } catch (e) {
            console.error(">> JSON send failed, falling back to raw send:", e);
            wsConnection.send(data);
          }
        } else {
          console.warn(">> Request WS not open! ReadyState:", wsConnection?.readyState);
        }
      });
    } catch (error) {
      console.error("Terminal initialization error:", error);
      toast.error("Failed to initialize terminal");
    }
  };

  const handleLogout = async () => {
    // On logout, clear all terminal sessions for a clean restart.
    await sendEndCommand(null);

    try {
      if (wsRequestRef.current && wsRequestRef.current.readyState === WebSocket.OPEN) {
        wsRequestRef.current.close();
      }
      if (wsResponseRef.current && wsResponseRef.current.readyState === WebSocket.OPEN) {
        wsResponseRef.current.close();
      }
      if (effectCleanupRef.current) {
        effectCleanupRef.current.abort();
      }
    } catch (e) {
      console.error("Cleanup error:", e);
    }

    if (termRef.current) {
      termRef.current.dispose();
      termRef.current = null;
    }

    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    navigate("/devices");
  };

  const handleClearAllSessions = async () => {
    setIsClearingSessions(true);
    try {
      // End all sessions by sending null session_token
      await sendEndCommand(null);
      toast.success("All sessions cleared");
      // After clearing everything on backend, fully reset local state
      // so the normal initiate → websocket → login flow can start again.
      if (wsRequestRef.current && wsRequestRef.current.readyState === WebSocket.OPEN) {
        wsRequestRef.current.close();
      }
      if (wsResponseRef.current && wsResponseRef.current.readyState === WebSocket.OPEN) {
        wsResponseRef.current.close();
      }
      if (effectCleanupRef.current) {
        effectCleanupRef.current.abort();
      }
      if (termRef.current) {
        termRef.current.dispose();
        termRef.current = null;
      }

      setIsLoggedIn(false);
      setIsDeviceReady(false);
      setIsWebSocketConnected(false);
      setConnectionError(null);
      setWsRequestEndpoint(null);
      setWsResponseEndpoint(null);

      initiationAttemptedRef.current = false;
      setSessionToken(generateSessionToken());
    } catch (err) {
      console.error("Clear all sessions error:", err);
      toast.error("Failed to clear sessions");
    } finally {
      setIsClearingSessions(false);
    }
  };

  const handleBack = () => {
    // If this screen is opened as a pop-out, close it.
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }

    // Otherwise go back in SPA history, with a safe fallback.
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/devices");
  };

  const pageStyle = {
    margin: 0,
    background: "#1e1e1e",
    color: "#ddd",
    fontFamily: "monospace",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  };

  const loginWrapperStyle = {
    flex: 1,
    display:
      isLoggedIn || isPreparingSession || (isPopoutMode && !hasPreClearedSessions)
        ? "none"
        : "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const loginBoxStyle = {
    background: "#2a2a2a",
    padding: 30,
    borderRadius: 6,
    width: 320,
    boxShadow: "0 0 20px rgba(0,0,0,0.5)",
  };

  const terminalContainerStyle = {
    flex: 1,
    display: isLoggedIn ? "flex" : "none",
    flexDirection: "column",
    height: "100vh",
  };

  const headerStyle = {
    background: "#2a2a2a",
    padding: "8px 12px",
    fontSize: 13,
    color: "#00ff88",
    borderBottom: "1px solid #444",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const terminalDivStyle = {
    flex: 1,
  };

  return (
    <div style={pageStyle}>
      {isPreparingSession && (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#00ff88",
            fontSize: 14,
          }}
        >
          Preparing terminal session...
        </div>
      )}

      {/* LOGIN VIEW */}
      <div style={loginWrapperStyle}>
        <form style={loginBoxStyle} onSubmit={handleLoginSubmit}>
          <h2
            style={{
              textAlign: "center",
              color: "#00ff88",
              marginBottom: 20,
            }}
          >
            Remote Terminal
          </h2>
          <p
            style={{
              textAlign: "center",
              fontSize: 12,
              marginBottom: 10,
              color: "#aaa",
              wordBreak: "break-all",
            }}
          >
            Device: <strong style={{ color: "#00ff88" }}>{hubName}</strong>
          </p>

          <input
            name="username"
            placeholder="Username"
            required
            style={{
              width: "100%",
              marginBottom: 12,
              padding: 10,
              background: "#111",
              border: "1px solid #444",
              color: "#ddd",
              fontFamily: "monospace",
              fontSize: 14,
            }}
            disabled={isLoading || !isDeviceReady}
          />
          <input
            name="password"
            type="password"
            placeholder="Password"
            required
            style={{
              width: "100%",
              marginBottom: 12,
              padding: 10,
              background: "#111",
              border: "1px solid #444",
              color: "#ddd",
              fontFamily: "monospace",
              fontSize: 14,
            }}
            disabled={isLoading || !isDeviceReady}
          />

          <button
            type="submit"
            style={{
              width: "100%",
              marginBottom: 12,
              padding: 10,
              background: "#00ff88",
              border: "none",
              fontWeight: "bold",
              cursor: isLoading || !isDeviceReady ? "not-allowed" : "pointer",
              fontFamily: "monospace",
              fontSize: 14,
              opacity: isLoading || !isDeviceReady ? 0.6 : 1,
            }}
            disabled={isLoading || !isDeviceReady}
          >
            {isLoading ? "Logging in..." : isDeviceReady ? "Login" : "Waiting..."}
          </button>

          <button
            type="button"
            onClick={handleClearAllSessions}
            style={{
              width: "100%",
              marginBottom: 0,
              padding: 10,
              background: "#ff6b6b",
              border: "none",
              fontWeight: "bold",
              cursor: isClearingSessions ? "not-allowed" : "pointer",
              fontFamily: "monospace",
              fontSize: 14,
              opacity: isClearingSessions ? 0.6 : 1,
              marginTop: 8,
            }}
            disabled={isClearingSessions}
          >
            {isClearingSessions ? "Clearing..." : "Clear All Sessions"}
          </button>

          {connectionError && (
            <div
              style={{
                color: "#ff5555",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              {connectionError}
            </div>
          )}
        </form>
      </div>

      {/* TERMINAL VIEW */}
      <div style={terminalContainerStyle}>
        <div style={headerStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span>
              Connected to <span style={{ color: "#00ff88" }}>{hubName}</span>
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleLogout}
              style={{
                padding: "6px 12px",
                background: "#ff5555",
                border: "none",
                borderRadius: 4,
                color: "#fff",
                fontSize: 13,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Logout
            </button>
          </div>
        </div>
        <div style={terminalDivStyle} ref={terminalContainerRef} />
      </div>
    </div>
  );
};

export default RemoteTerminal;
